import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import type { AuthUser, MeResponse } from '@/api/types';

type AuthState = {
  user: AuthUser | null;
  authLevel: 'partial' | 'full' | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

async function fetchMe(): Promise<MeResponse | null> {
  try {
    return await apiFetch<MeResponse>('/api/auth/me', { skipAuthRedirect: true });
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: fetchMe,
    retry: false,
  });

  const user = meQuery.data?.user ?? null;
  const authLevel = meQuery.data?.authLevel ?? null;
  const loading = meQuery.isLoading;

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ['auth', 'me'] });
  }, [qc]);

  const logout = useCallback(async () => {
    await apiFetch('/api/auth/logout', { method: 'POST', skipAuthRedirect: true });
    await qc.resetQueries({ queryKey: ['auth', 'me'] });
    window.location.assign('/login');
  }, [qc]);

  const value = useMemo(
    () => ({ user, authLevel, loading, refresh, logout }),
    [user, authLevel, loading, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth binnen AuthProvider gebruiken');
  return ctx;
}
